/* eslint-disable no-underscore-dangle */
import { INITIAL_VALUES, QMASDocumentModal } from 'components/Molecules/Upload/QMASDocument/index';
import React from 'react';
import classes from './SaveToAndSetData.module.scss';
import get from 'lodash/get';
import { castToMoment } from 'helpers/index';
import { useList } from 'context/ResourceProvider/hooks/useList';
import { ComplianceMappings } from 'mappers/ComplianceDataMapper';

function SaveToAndSetData({ open, onCancel, onSubmit, documents = [], metadata }) {
  const formRef = React.useRef(null);
  const featureFlags = get(metadata, 'featureFlags', undefined);

  const firstDocumentId = React.useMemo(() => {
    if (documents.length > 1) {
      return null;
    }
    const { documentListId, documentList } = documents[0];
    if (documentListId) return documentListId;
    if (documentList) return documentList.id;

    return documents[0].id;
  }, [documents]);

  const { data: testData, loading: testDataLoading } = useList({
    resource: 'document-list-compliance-test-data',
    meta: {
      params: {
        id: firstDocumentId,
      },
    },
    skip: !firstDocumentId,
  });

  const { data: certificateData, loading: certificateDataLoading } = useList({
    resource: 'document-list-compliance-certificate-data',
    meta: {
      params: {
        id: firstDocumentId,
      },
    },
    skip: !firstDocumentId,
  });

  const { data: otherData, loading: otherDataLoading } = useList({
    resource: 'document-list-compliance-other-data',
    meta: {
      params: {
        id: firstDocumentId,
      },
    },
    skip: !firstDocumentId,
  });

  const complianceLoading = React.useMemo(() => {
    return testDataLoading || certificateDataLoading || otherDataLoading;
  }, [testDataLoading, certificateDataLoading, otherDataLoading]);

  const complianceData = React.useMemo(() => {
    if (featureFlags && featureFlags.includes('compliance-data')) {
      const _testData = get(testData, 'data.data', []);
      const _certificateData = get(certificateData, 'data.data', []);
      const _otherData = get(otherData, 'data.data', []);
      return {
        testData: (_testData || []).map((item) => ComplianceMappings.mapTestDataToView(item)),
        certificateData: (_certificateData || []).map((item) =>
          ComplianceMappings.mapCertificateDataToView(item)
        ),
        otherData: (_otherData || []).map((item) => ComplianceMappings.mapOtherDataToView(item)),
      };
    }
    return null;
  }, [testData, certificateData, otherData]);

  const previousFactories = React.useMemo(() => {
    if (documents.length > 1) {
      return [];
    }
    const factories = get(documents, '[0].factories', []);
    return factories;
  }, [documents]);

  const previousSuppliers = React.useMemo(() => {
    if (documents.length > 1) {
      return [];
    }
    const suppliers = get(documents, '[0].suppliers', []);
    return suppliers;
  }, [documents]);

  const previousProducts = React.useMemo(() => {
    if (documents.length > 1) {
      return [];
    }
    const products = get(documents, '[0].products', []);
    return products;
  }, [documents]);

  const initialValues = React.useMemo(() => {
    if (documents.length > 1) {
      return INITIAL_VALUES;
    }
    const firstDocument = documents[0];
    const initial = {
      type: firstDocument.type ? [firstDocument.type.id] : [],
      saveTo: {
        products: (previousProducts || []).map(({ ...rest }) => ({ ...rest, deletable: false })),
        suppliers: (previousSuppliers || []).map(({ ...rest }) => ({
          ...rest,
          deletable: false,
        })),
        factories: (previousFactories || []).map(({ ...rest }) => ({
          ...rest,
          deletable: false,
        })),
      },
      reminder: {
        issueDate: castToMoment(firstDocument.issueDate),
        expiryDate: castToMoment(firstDocument.expiryDate),
        reviewDate: castToMoment(firstDocument.reviewDate),
        reminderDate: castToMoment(firstDocument.reminderDate),
        reminderType: firstDocument.reminderType,
        note: firstDocument.note,
      },
    };
    if (featureFlags && featureFlags.includes('compliance-data')) {
      return {
        ...initial,
        complianceData: {
          ...complianceData,
        },
      };
    }
  }, [documents, complianceData]);

  return (
    <QMASDocumentModal
      destroyOnclose
      width={1100}
      onCancel={onCancel}
      onOk={() => {
        if (formRef.current) {
          formRef.current.submit();
        }
      }}
      onSubmit={(values, form) => {
        onSubmit(values, form);
      }}
      childRef={formRef}
      title="Save To & Set Data"
      open={open}
      files={documents}
      wrapClassName={classes.overlayModal}
      initialValues={{
        ...initialValues,
        files: documents,
      }}
      cancelButtonProps={{ hidden: true }}
      okText="Confirm"
      relatedSource={metadata.related || {}}
      featureFlags={featureFlags || ['saveTo', 'reminder']}
      loading={complianceLoading}
    />
  );
}

export default SaveToAndSetData;
